import React, { useState } from 'react';
import { backdropProps } from '../utils/backdropClose';
import { supabase } from '../supabase';
import { US_STATES } from '../constants/usStates';
import { MONTHS } from '../constants/months';
import { DAYS, PICK_YEARS, REAPPRAISAL_CYCLES, YESNO, FISCAL_YEARS, composeCompliance, composeSeasonMY,
  RATIO_OPTIONS, ratioIn, ratioOut, seasonYears, parseMonthYear, parseComplianceMY, composeComplianceMY, parseMonthDay,
  shiftYears, embedsCalendarYear, SEEDABLE_ASSESSOR_TEMPORAL } from '../constants/refFields';
import { entityKey } from '../utils/entityKey';
import { pageAll } from '../utils/pageAll';
import { EXEMPT_TEXT, BPP_EXEMPT_FIELDS, BPP_OTHER_FIELDS, isBppExempt } from '../utils/assessorExempt';

// Add/Edit Assessor — TWO-TRACK (RE / BPP) + VERSION-AWARE.
//
// Shape mirrors the two-track importer exactly (ImportTemplateModalTwoTrack): same columns, same value
// formats. Hand-created assessors must be structurally identical to imported ones, or a re-import would
// see them as different rows.
//
// Versioning: assessors are grained (entity_id, value_year). entity_id is the stable anchor parcels FK;
// each year is its own row. So:
//   ADD  — resolve the entity by (state, entityKey(name)) exactly as the importer does. Found → add a new
//          YEAR-VERSION under that same entity. Not found → mint an entity_id, insert the anchor, then the
//          first version. Never mint a second entity for an office that already exists (that splits it).
//   EDIT — update the year-scoped row by id. entity_id / value_year / county / active are not touched.
//
// BACKFILL SEEDING: `seed` is the nearest existing version of this same entity, used to create an empty
// prior year. Every field carries, and a calendar year inside a season is SHIFTED by the gap between the two
// records ("October 2026" on 2026 → "October 2025" on 2025) — Philip, 2026-10-09; it used to be blanked. A
// shifted year is still a guess, so the record keeps seeded_from_year (the list's warning) until someone opens
// it and ticks "Verified".
//
// TEMPLATE (Philip, 2026-10-09), RE and BPP alike: Ratio is a dropdown (Other… for anything else); Assessment Date
// is Month + Day only; Compliance Season is Month + Year with N/A and Other (relative) — the only field keeping them;
// Notice and Appeal Season are Month + Year. A stored value the pickers cannot hold is kept and shown as
// "previously: …" until someone sets a value — never converted in bulk, never dropped on a save.
//
// Props: isDark; assessor (null=add / row=edit); seed (nearest-year row to pre-fill from, add only);
//        defaultState; valueYear (screen-selected year, locked); onClose; onSuccess(row).
const TRACKS = [
  { key: 're', label: 'Real Estate' },
  { key: 'pp', label: 'Business Personal Property' },
];
// Human labels for the blanked-field list in the seed banner.
const FIELD_LABELS = {
  assessment_date: 'Assessment Date', reappraisal_cycle: 'Reappraisal Cycle', fiscal_year: 'Fiscal Year',
  compliance_season: 'Compliance Season', extensions: 'Extensions',
  notice_season: 'Notice Season', appeal_season: 'Appeal Season',
};
const LABELS = Object.fromEntries(TRACKS.flatMap(({ key: t, label }) =>
  Object.entries(FIELD_LABELS).map(([f, l]) => [`${t}_${f}`, `${t === 're' ? 'RE' : 'BPP'} ${l}`])));

export default function AddAssessorModal({ isDark, assessor, seed, defaultState, valueYear, onClose, onSuccess }) {
  const isEdit = !!assessor;
  const isSeeded = !assessor && !!seed;          // creating a prior year pre-filled from another version
  const src = assessor || seed || null;          // where the initial field values come from
  const lockedYear = valueYear ?? assessor?.value_year ?? ''; // Value Year is read-only = the screen's year

  // Which temporal fields this seed SHIFTS because their stored value names a calendar year. Computed from the
  // DATA, not a fixed list — the same column names a year on one assessor and not on another. Drives the banner
  // and whether the new record stays flagged.
  const yearShift = isSeeded ? (Number(lockedYear) - Number(seed.value_year)) || 0 : 0;
  const shiftedKeys = React.useMemo(() => {
    if (!isSeeded) return [];
    const out = [];
    TRACKS.forEach(({ key: t }) => SEEDABLE_ASSESSOR_TEMPORAL.forEach(f => {
      if (embedsCalendarYear(seed[`${t}_${f}`])) out.push(`${t}_${f}`);
    }));
    return out;
  }, [isSeeded, seed]);
  const [markVerified, setMarkVerified] = useState(false); // reopened flagged record — Steph clears it explicitly

  // One stored text column → several form keys each. `_legacy` holds a stored value the pickers cannot show.
  const mdIn = (prefix, stored) => {
    const p = parseMonthDay(stored);
    return { [`${prefix}_month`]: p.month, [`${prefix}_day`]: p.day, [`${prefix}_legacy`]: p.legacy };
  };
  const myIn = (prefix, stored) => {
    const p = parseMonthYear(stored);
    return { [`${prefix}_month`]: p.month, [`${prefix}_year`]: p.year, [`${prefix}_legacy`]: p.legacy };
  };
  const cmpIn = (prefix, stored) => {
    const p = parseComplianceMY(stored, lockedYear);   // a bare "Month D" takes this record's value year (decision 4)
    return { [`${prefix}_month`]: p.month, [`${prefix}_year`]: p.year, [`${prefix}_na`]: p.na,
      [`${prefix}_other_on`]: !!p.other, [`${prefix}_other`]: p.other };
  };

  const [form, setForm] = useState(() => {
    const base = {
      name:              src?.name || '',
      state:             src?.state || defaultState || 'AL',
      url:               src?.url || '',
      mailing_address_1: src?.mailing_address_1 || '',
      mailing_address_2: src?.mailing_address_2 || '',
      mailing_city:      src?.mailing_city || '',
      mailing_state:     src?.mailing_state || '',
      mailing_zip:       src?.mailing_zip || '',
      // BPP exempt is a text signal ("Exempt" in the BPP_EXEMPT_FIELDS), not a boolean column. Initialize
      // the checkbox by detecting it on the loaded record; an exempt state carries across a seed unchanged
      // (the word "Exempt" holds no year, so it is never blanked).
      pp_exempt:         isBppExempt(src),
    };
    // On a seed, a calendar year inside a temporal value moves by the gap between the records; everything else
    // carries as is. reappraisal_year is the explicit exception — a bare year that is schedule, not value-year.
    const carry = (t, f) => shiftYears(src?.[`${t}_${f}`] || '', yearShift);
    TRACKS.forEach(({ key: t }) => {
      const ratio = ratioIn(src?.[`${t}_ratio`]);                             // numeric — no year
      Object.assign(base,
        mdIn(`${t}_asmt`, carry(t, 'assessment_date') || null),
        cmpIn(`${t}_comp`, carry(t, 'compliance_season') || null),
        myIn(`${t}_notice`, carry(t, 'notice_season') || null),
        myIn(`${t}_appeal`, carry(t, 'appeal_season') || null),
        {
          [`${t}_reappraisal_year`]:  src?.[`${t}_reappraisal_year`] ?? '',   // always carries
          [`${t}_reappraisal_cycle`]: carry(t, 'reappraisal_cycle'),
          [`${t}_fiscal_year`]:       carry(t, 'fiscal_year'),
          [`${t}_ratio_sel`]:         ratio.sel,
          [`${t}_ratio_other`]:       ratio.other,
          [`${t}_extensions`]:        carry(t, 'extensions'),
        });
    });
    return base;
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const c = isDark
    ? { card: '#2F3854', border: '#374060', text: '#e8eaf2', muted: 'var(--slate)', input: 'var(--ink)', inputBorder: '#434C68' }
    : { card: '#ffffff', border: 'var(--line)', text: 'var(--ink)', muted: 'var(--slate)', input: '#ffffff', inputBorder: '#c8ccd8' };
  const s = {
    label:    { display: 'block', fontSize: '11px', fontWeight: '700', color: c.muted, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '4px' },
    input:    { width: '100%', padding: '8px 11px', background: c.input, border: `1px solid ${c.inputBorder}`, borderRadius: '7px', color: c.text, fontSize: '13px', outline: 'none', boxSizing: 'border-box', fontFamily: "'Public Sans',system-ui,sans-serif" },
    btn:      { padding: '9px 18px', background: 'linear-gradient(135deg,#B40000,#9C0000)', border: 'none', borderRadius: '7px', color: 'white', fontSize: '13px', fontWeight: '700', cursor: 'pointer', fontFamily: "'Public Sans',system-ui,sans-serif" },
    btnGhost: { padding: '9px 16px', background: 'transparent', border: `1px solid ${c.border}`, borderRadius: '7px', color: c.muted, fontSize: '13px', cursor: 'pointer', fontFamily: "'Public Sans',system-ui,sans-serif" },
    sectionH: { fontSize: '12px', fontWeight: '700', color: c.text, textTransform: 'uppercase', letterSpacing: '0.06em', margin: '20px 0 12px', paddingBottom: '6px', borderBottom: `1px solid ${c.border}` },
  };

  const set = (f, v) => setForm(prev => ({ ...prev, [f]: v }));
  // Checking hides the BPP track (it's written "Exempt" on save regardless of these fields). Unchecking
  // re-shows it — so scrub any leftover literal "Exempt" a loaded/imported exempt record carried, but
  // ONLY that, so values the user actually typed this session survive a check→uncheck.
  const isExemptText = (v) => String(v || '').trim().toLowerCase() === EXEMPT_TEXT.toLowerCase();
  const setPpExempt = (checked) => {
    if (checked) { set('pp_exempt', true); return; }
    setForm(prev => {
      const next = { ...prev, pp_exempt: false };
      ['pp_reappraisal_cycle', 'pp_fiscal_year', 'pp_extensions', 'pp_asmt_legacy', 'pp_notice_legacy', 'pp_appeal_legacy']
        .forEach(k => { if (isExemptText(prev[k])) next[k] = ''; });   // "Exempt" lands as a legacy value on load
      if (isExemptText(prev.pp_comp_other)) Object.assign(next,   // …and as free-text Other on Compliance Season
        { pp_comp_month: '', pp_comp_year: '', pp_comp_na: false, pp_comp_other_on: false, pp_comp_other: '' });
      return next;
    });
  };
  const num = (v) => (v === '' || v == null ? null : Number(v));
  const txt = (v) => (v && v.trim() ? v.trim() : null);
  // A picked value wins; with nothing picked, a legacy value is written back unchanged (kept until replaced).
  const mdOut = (prefix) => composeCompliance(form[`${prefix}_month`], form[`${prefix}_day`], false, '') || txt(form[`${prefix}_legacy`]);
  const myOut = (prefix) => composeSeasonMY(form[`${prefix}_month`], form[`${prefix}_year`]) || txt(form[`${prefix}_legacy`]);
  const cmpOut = (prefix) => composeComplianceMY(
    form[`${prefix}_month`], form[`${prefix}_year`], form[`${prefix}_na`],
    form[`${prefix}_other_on`] ? form[`${prefix}_other`] : '');

  // county is intentionally NOT written here — importer-only (dedup key); preserved on edit by omission.
  // active is omitted too: new rows take the DB default (true) and edits keep any retire state, matching
  // the importer. assessment_ratio mirrors re_ratio — the legacy shared column ParcelModal still reads
  // (migration FLAG 1); it goes away in increment 5 when ParcelModal reads per-track.
  const fields = () => {
    const out = {
      name: txt(form.name), state: form.state || null,
      value_year: num(lockedYear),
      url: txt(form.url),
      mailing_address_1: txt(form.mailing_address_1), mailing_address_2: txt(form.mailing_address_2),
      mailing_city: txt(form.mailing_city), mailing_state: txt(form.mailing_state), mailing_zip: txt(form.mailing_zip),
    };
    TRACKS.forEach(({ key: t }) => {
      out[`${t}_assessment_date`]   = mdOut(`${t}_asmt`);
      out[`${t}_reappraisal_year`]  = num(form[`${t}_reappraisal_year`]);
      out[`${t}_reappraisal_cycle`] = txt(form[`${t}_reappraisal_cycle`]);
      out[`${t}_fiscal_year`]       = txt(form[`${t}_fiscal_year`]);
      out[`${t}_ratio`]             = ratioOut(form[`${t}_ratio_sel`], form[`${t}_ratio_other`]);
      out[`${t}_compliance_season`] = cmpOut(`${t}_comp`);
      out[`${t}_extensions`]        = txt(form[`${t}_extensions`]);
      out[`${t}_notice_season`]     = myOut(`${t}_notice`);
      out[`${t}_appeal_season`]     = myOut(`${t}_appeal`);
    });
    out.assessment_ratio = out.re_ratio;
    // BPP exempt overrides the whole pp track: write "Exempt" into the detection fields (what the detail
    // reads) and null the rest, matching how an imported exempt assessor looks. Unchecking falls through
    // to the normal pp values written above, so the fields become real again.
    if (form.pp_exempt) {
      BPP_EXEMPT_FIELDS.forEach(k => { out[k] = EXEMPT_TEXT; });
      BPP_OTHER_FIELDS.forEach(k => { out[k] = null; });
    }
    // Seeded provenance. On a seed: flagged when any year was shifted (a shifted year is a guess until someone
    // checks it) — a shifted pp_ field is moot when the track is exempt (stored null on purpose). On a reopened
    // flagged record: cleared only when Steph ticks "verified".
    const pending = shiftedKeys.filter(k => !(form.pp_exempt && k.startsWith('pp_')));
    out.seeded_from_year = isSeeded
      ? (pending.length ? Number(seed.value_year) : null)
      : (markVerified ? null : (assessor?.seeded_from_year ?? null));
    return out;
  };

  // Resolve this office's entity_id the way the importer would: state-scoped, on the office-suffix-stripped
  // CORE name. Returns an existing entity_id, or mints one + inserts its anchor row.
  async function resolveEntityId(name, state) {
    const key = entityKey(name);
    // pageAll: this state's assessors can exceed the 1000-row cap once big states load — an unpaginated read would
    // silently truncate, miss an existing entity past the cut, and mint a DUPLICATE. Page it.
    const data = await pageAll(() => supabase.from('assessors').select('entity_id,name').eq('state', state));
    const hit = (data || []).find(a => entityKey(a.name) === key);
    if (hit) return hit.entity_id;
    const eid = crypto.randomUUID();
    const { error: ae } = await supabase.from('assessor_entities').insert({ entity_id: eid });
    if (ae) throw ae;
    return eid;
  }

  async function handleSave() {
    if (!form.name.trim()) { setError('Name is required.'); return; }
    if (!form.state) { setError('State is required.'); return; }
    if (!lockedYear) { setError('No value year selected.'); return; }
    setSaving(true); setError('');
    try {
      let data, e;
      if (isEdit) {
        ({ data, error: e } = await supabase.from('assessors').update(fields()).eq('id', assessor.id).select().single());
      } else {
        const entity_id = await resolveEntityId(form.name, form.state);
        ({ data, error: e } = await supabase.from('assessors').insert({ ...fields(), entity_id }).select().single());
      }
      if (e) throw e;
      onSuccess(data);
    } catch (ex) {
      // 23505 on (entity_id, value_year) = this office already has a row for this year.
      setError(ex?.code === '23505'
        ? `${form.name.trim()} already has a ${lockedYear} record. Edit that one instead of adding a second.`
        : (ex?.message || 'Save failed.'));
    } finally {
      setSaving(false);
    }
  }

  const monthSel = (prefix, disabled) => (
    <select style={{ ...s.input, opacity: disabled ? 0.5 : 1 }} disabled={disabled} value={form[`${prefix}_month`]} onChange={e => set(`${prefix}_month`, e.target.value)}>
      <option value="">Month</option>
      {MONTHS.map(m => <option key={m.value} value={m.value}>{m.label}</option>)}
    </select>
  );
  const yearSel = (prefix, disabled) => (
    <select style={{ ...s.input, maxWidth: '90px', opacity: disabled ? 0.5 : 1 }} disabled={disabled} value={form[`${prefix}_year`]} onChange={e => set(`${prefix}_year`, e.target.value)}>
      <option value="">Year</option>
      {seasonYears(lockedYear, form[`${prefix}_year`]).map(y => <option key={y} value={y}>{y}</option>)}
    </select>
  );
  // A stored value the pickers cannot hold: shown, kept on save, replaced the moment a value is picked.
  const legacyNote = (prefix, picked) => (form[`${prefix}_legacy`] && !picked) ? (
    <div style={{ fontSize: '11px', color: c.muted, marginTop: '5px' }}>
      previously: <span style={{ color: c.text }}>{form[`${prefix}_legacy`]}</span>{' '}
      <span onClick={() => set(`${prefix}_legacy`, '')} style={{ color: 'var(--red)', cursor: 'pointer', borderBottom: '1px dotted var(--red)' }}>clear</span>
    </div>
  ) : null;

  // Assessment Date: Month + Day.
  const monthDayField = (label, prefix) => (
    <div>
      <label style={s.label}>{label}</label>
      <div style={{ display: 'flex', gap: '6px' }}>
        {monthSel(prefix, false)}
        <select style={{ ...s.input, maxWidth: '70px' }} value={form[`${prefix}_day`]} onChange={e => set(`${prefix}_day`, e.target.value)}>
          <option value="">Day</option>
          {DAYS.map(d => <option key={d} value={d}>{d}</option>)}
        </select>
      </div>
      {legacyNote(prefix, form[`${prefix}_month`] && form[`${prefix}_day`])}
    </div>
  );
  // Notice / Appeal Season: Month + Year.
  const monthYearField = (label, prefix) => (
    <div>
      <label style={s.label}>{label}</label>
      <div style={{ display: 'flex', gap: '6px' }}>{monthSel(prefix, false)}{yearSel(prefix, false)}</div>
      {legacyNote(prefix, form[`${prefix}_month`] && form[`${prefix}_year`])}
    </div>
  );
  // Compliance Season: Month + Year, or N/A, or Other (relative) free text — the only field with N/A and Other.
  const complianceField = (label, prefix) => {
    const na = form[`${prefix}_na`], other = form[`${prefix}_other_on`];
    const chk = { display: 'flex', alignItems: 'center', gap: '4px', fontSize: '11px', color: c.muted, cursor: 'pointer', whiteSpace: 'nowrap' };
    return (
      <div>
        <label style={s.label}>{label}</label>
        {other
          ? <input style={s.input} value={form[`${prefix}_other`]} onChange={e => set(`${prefix}_other`, e.target.value)} placeholder="e.g. 3rd Monday in January" />
          : <div style={{ display: 'flex', gap: '6px' }}>{monthSel(prefix, na)}{yearSel(prefix, na)}</div>}
        <div style={{ display: 'flex', gap: '14px', marginTop: '5px' }}>
          <label style={chk}><input type="checkbox" checked={na} onChange={e => { set(`${prefix}_na`, e.target.checked); if (e.target.checked) set(`${prefix}_other_on`, false); }} /> N/A</label>
          <label style={chk}><input type="checkbox" checked={other} onChange={e => { set(`${prefix}_other_on`, e.target.checked); if (e.target.checked) set(`${prefix}_na`, false); }} /> Other (relative)</label>
        </div>
      </div>
    );
  };
  // Ratio: the list, or Other… for anything else (an off-list stored value opens there, unchanged).
  const ratioField = (t) => (
    <div><label style={s.label}>Ratio</label>
      <select style={s.input} value={form[`${t}_ratio_sel`]} onChange={e => set(`${t}_ratio_sel`, e.target.value)}>
        <option value="">—</option>
        {RATIO_OPTIONS.map(p => <option key={p} value={String(p)}>{p}%</option>)}
        <option value="other">Other…</option>
      </select>
      {form[`${t}_ratio_sel`] === 'other' && (
        <input style={{ ...s.input, marginTop: '6px' }} value={form[`${t}_ratio_other`]} onChange={e => set(`${t}_ratio_other`, e.target.value)} placeholder="e.g. 11.5%" />
      )}
    </div>
  );

  const pick = (label, field, opts) => (
    <div><label style={s.label}>{label}</label>
      <select style={s.input} value={form[field]} onChange={e => set(field, e.target.value)}>
        <option value="">—</option>{opts.map(o => <option key={o} value={o}>{o}</option>)}
      </select>
    </div>
  );

  // The per-track fields, identical for RE and BPP, in Philip's 2026-10-09 layout.
  const trackSection = (t) => {
    if (t === 'pp' && form.pp_exempt) {
      return <div style={{ fontSize: '13px', color: c.muted, fontStyle: 'italic', marginBottom: '14px' }}>Exempt — Business Personal Property is not assessed here; its fields are stored as “Exempt”.</div>;
    }
    const row = (cols, kids) => <div style={{ display: 'grid', gridTemplateColumns: cols, gap: '10px', marginBottom: '14px' }}>{kids}</div>;
    return (
    <>
      {row('1.5fr 1fr 1fr', <>
        {monthDayField('Assessment Date', `${t}_asmt`)}
        {pick('Reappraisal Year', `${t}_reappraisal_year`, PICK_YEARS)}
        {pick('Re-appraisal Cycle', `${t}_reappraisal_cycle`, REAPPRAISAL_CYCLES)}
      </>)}
      {row('2fr 1fr', <>
        {pick('Fiscal Year', `${t}_fiscal_year`, FISCAL_YEARS)}
        {ratioField(t)}
      </>)}
      {row('1.5fr 1fr', <>
        {complianceField('Compliance Season', `${t}_comp`)}
        {pick('Extensions', `${t}_extensions`, YESNO)}
      </>)}
      {row('1fr 1fr', <>
        {monthYearField('Notice Season', `${t}_notice`)}
        {monthYearField('Appeal Season', `${t}_appeal`)}
      </>)}
    </>
    );
  };

  return (
    <div {...backdropProps(onClose)} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px' }}>
      <div onClick={e => e.stopPropagation()} style={{ background: c.card, borderRadius: '14px', padding: '28px', maxWidth: '680px', width: '100%', boxShadow: '0 8px 40px rgba(0,0,0,0.3)', maxHeight: 'calc(100vh - 40px)', overflowY: 'auto', fontFamily: "'Public Sans',system-ui,sans-serif" }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '12px' }}>
          <div style={{ fontFamily: "'Archivo',sans-serif", fontSize: '20px', fontWeight: '700', color: c.text }}>{isEdit ? 'Edit Assessor' : 'Add Assessor'}</div>
          <button onClick={onClose} type="button" style={{ background: 'none', border: 'none', color: c.muted, fontSize: '18px', cursor: 'pointer', padding: '0 4px', lineHeight: 1 }}>✕</button>
        </div>

        {isSeeded && (
          <div style={{ background: 'rgba(234,179,8,0.12)', border: '1px solid rgba(234,179,8,0.45)', borderRadius: '8px', padding: '10px 12px', marginBottom: '14px', fontSize: '12px', color: c.text, lineHeight: 1.5 }}>
            <strong>⚠ Seeded from {seed.value_year}.</strong> Every field was copied from that year — review each one.
            {shiftedKeys.length > 0
              ? <> These named a year, which was <strong>moved from {seed.value_year} to {lockedYear}</strong> — check them:
                  <strong> {shiftedKeys.map(k => LABELS[k] || k).join(', ')}</strong>. This record stays flagged until it is
                  opened and ticked “Verified”.</>
              : <> Nothing in {seed.value_year} named a year, so every field carried across unchanged.</>}
          </div>
        )}
        {isEdit && assessor?.seeded_from_year != null && (
          <div style={{ background: 'rgba(234,179,8,0.12)', border: '1px solid rgba(234,179,8,0.45)', borderRadius: '8px', padding: '10px 12px', marginBottom: '14px', fontSize: '12px', color: c.text, lineHeight: 1.5 }}>
            <strong>⚠ Seeded from {assessor.seeded_from_year} and not yet verified.</strong> Check the year-specific fields
            below are right for {lockedYear}, then tick this to clear the flag.
            <label style={{ display: 'flex', alignItems: 'center', gap: '6px', marginTop: '8px', cursor: 'pointer', fontWeight: '700' }}>
              <input type="checkbox" checked={markVerified} onChange={e => setMarkVerified(e.target.checked)} />
              Verified for {lockedYear}
            </label>
          </div>
        )}

        <div style={s.sectionH}>General</div>
        <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr', gap: '10px', marginBottom: '14px' }}>
          <div><label style={s.label}>Name *</label><input style={s.input} value={form.name} onChange={e => set('name', e.target.value)} placeholder="e.g. Baldwin County" /></div>
          <div><label style={s.label}>State *</label>
            <select style={s.input} value={form.state} onChange={e => set('state', e.target.value)}>
              {Object.keys(US_STATES).map(code => <option key={code} value={code}>{code}</option>)}
            </select>
          </div>
          <div><label style={s.label}>Value Year</label><input style={{ ...s.input, opacity: 0.6, cursor: 'not-allowed' }} value={lockedYear} readOnly title="Set by the year selected on the main screen" /></div>
        </div>
        <div style={{ marginBottom: '14px' }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '12px', color: c.text, cursor: 'pointer' }}
            title="No Business Personal Property assessment here — stores the BPP track as “Exempt”.">
            <input type="checkbox" checked={form.pp_exempt} onChange={e => setPpExempt(e.target.checked)} />
            Business Personal Property exempt
          </label>
        </div>
        <div style={{ marginBottom: '14px' }}><label style={s.label}>URL</label><input style={s.input} value={form.url} onChange={e => set('url', e.target.value)} placeholder="https://…" /></div>
        <div style={{ marginBottom: '14px' }}><label style={s.label}>Mailing Address 1</label><input style={s.input} value={form.mailing_address_1} onChange={e => set('mailing_address_1', e.target.value)} /></div>
        <div style={{ marginBottom: '14px' }}><label style={s.label}>Mailing Address 2</label><input style={s.input} value={form.mailing_address_2} onChange={e => set('mailing_address_2', e.target.value)} /></div>
        <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr', gap: '10px', marginBottom: '14px' }}>
          <div><label style={s.label}>Mailing City</label><input style={s.input} value={form.mailing_city} onChange={e => set('mailing_city', e.target.value)} /></div>
          <div><label style={s.label}>Mailing State</label><input style={s.input} value={form.mailing_state} onChange={e => set('mailing_state', e.target.value)} /></div>
          <div><label style={s.label}>Mailing Zip</label><input style={s.input} value={form.mailing_zip} onChange={e => set('mailing_zip', e.target.value)} /></div>
        </div>

        {TRACKS.map(({ key, label }) => (
          <React.Fragment key={key}>
            <div style={s.sectionH}>{label}</div>
            {trackSection(key)}
          </React.Fragment>
        ))}

        {error && <div style={{ color: '#f87171', fontSize: '12px', marginBottom: '10px' }}>{error}</div>}
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
          <button type="button" onClick={onClose} disabled={saving} style={s.btnGhost}>Cancel</button>
          <button type="button" onClick={handleSave} disabled={saving} style={s.btn}>{saving ? 'Saving…' : (isEdit ? 'Save Changes' : 'Add Assessor')}</button>
        </div>
      </div>
    </div>
  );
}
